/**
 * Tile-type picker cards (S6) — the ONE type-choosing surface. Selecting a
 * type switches the config section; `note` additionally hides every data
 * section (connection / SQL / refresh / preview). After a preview, the best
 * shape suggestion's card carries a corner sparkles pill (the cards are too
 * narrow for badge text — "Suggested" + the reason live in the tooltip) and
 * clicking a card with a suggestion applies its auto-config — the dialog's
 * select handler decides via `suggestionForType`.
 */
import { BarChart3, Grid3x3, Hash, Map as MapIcon, Sparkles, StickyNote, Table2 } from 'lucide-react';
import type { DashboardTileType } from '../../types/dashboardTypes';
import type { ShapeSuggestions } from '../../utils/shapeSuggest';
import { suggestedTileType, suggestionForType } from './tileTypeSuggestions';

const TYPES: Array<{ type: DashboardTileType; label: string; icon: typeof BarChart3 }> = [
  { type: 'chart', label: 'Chart', icon: BarChart3 },
  { type: 'stat', label: 'Stat', icon: Hash },
  { type: 'table', label: 'Table', icon: Table2 },
  { type: 'pivot', label: 'Pivot', icon: Grid3x3 },
  { type: 'map', label: 'Map', icon: MapIcon },
  { type: 'note', label: 'Note', icon: StickyNote },
];

export function TileTypeCards({
  selected,
  suggestions,
  onSelect,
}: {
  selected: DashboardTileType | '';
  /** Shape suggestions from the last preview — null before one completes. */
  suggestions: ShapeSuggestions | null;
  onSelect: (type: DashboardTileType) => void;
}) {
  const suggested = suggestedTileType(suggestions);
  return (
    // The suggestion badge sits INSIDE the card (top-right inset): the dialog's
    // grid half is an overflow container, so a negatively-offset corner pill
    // was clipped against the card border on the top row. Every card carries
    // the same extra top padding so nothing shifts when a suggestion arrives.
    <div className="grid grid-cols-6 gap-1.5" role="radiogroup" aria-label="Tile type">
      {TYPES.map(({ type, label, icon: Icon }) => {
        const isSelected = selected === type;
        const isSuggested = suggested === type;
        const suggestion = suggestionForType(suggestions, type);
        return (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onSelect(type)}
            title={isSuggested && suggestion ? `Suggested — ${suggestion.reason}` : suggestion?.reason}
            className={`relative flex flex-col items-center gap-1 rounded-md border px-2 pb-2 pt-3.5 text-xs transition-colors hover:bg-accent ${
              isSelected
                ? 'border-primary bg-primary/5 text-foreground'
                : isSuggested
                  ? 'border-primary/50 bg-primary/5 text-muted-foreground'
                  : 'border-border text-muted-foreground'
            }`}
            data-testid={`tile-type-card-${type}`}
            data-tile-type={type}
            data-selected={isSelected || undefined}
            data-suggested={isSuggested || undefined}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {label}
            {isSuggested && (
              <span className="absolute right-1 top-1 rounded-full border border-primary/40 bg-popover p-[3px] text-primary shadow-sm">
                <Sparkles className="h-2.5 w-2.5" aria-hidden />
                <span className="sr-only">Suggested</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
